import Markdown from 'react-markdown';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import { Box } from '@mui/material';
import { Typography } from '@tkww/the-bash-frontend';

import SectionContainer from 'components/grid/SectionContainer';
import EditorialContainer from 'components/grid/EditorialContainer';
import SocialShareButtons from 'components/SocialShareButtons';
import ThemeIngredient from 'views/ThemePage/ThemeIngredient';
import RelatedThemes from 'components/RelatedThemes';
import { analyticsProperties, carouselTypes } from 'analytics/carousel';
import { paragraph, link } from 'helpers/MarkdownRenderers';
import getReactPlayerConfig from 'helpers/media/getReactPlayerConfig';

import ThemeHeader from './ThemeHeader';

const ThemePage = (props) => {
  const { theme, searchResultCollections, themeCount } = props;
  const [showPinterestButton, setShowPinterestButton] = useState(false);
  const isPinterestInitialized = useRef(false);

  useEffect(() => {
    setShowPinterestButton(true);
  }, []);

  useEffect(() => {
    if (showPinterestButton && window.initPinterestWidgets && !isPinterestInitialized.current) {
      window.initPinterestWidgets();
    }
  }, [showPinterestButton]);

  const handleVideoError = (error) => {
    console.error('Theme video playback error:', error);
  };

  return (
    <SectionContainer
      sx={(theme) => ({
        paddingTop: 3,
        '& .markdown': {
          '& h1, & h2, & h3, & h4, & h5': {
            margin: '0 0 8px',
            lineHeight: 1.2,
            color: theme.palette.text.primary,
          },
          '& p': {
            margin: theme.spacing(0, 0, 1),
            lineHeight: '22px',
            fontWeight: 300,
            fontSize: 16,
            color: theme.palette.text.primary,
            '&.large': {
              fontSize: 18,
              fontWeight: 300,
              lineHeight: '26px',
              color: theme.palette.text.primary,
              margin: '0 0 8px',
              letterSpacing: 0,
            },
          },
        },
      })}
    >
      <ThemeHeader theme={theme} showPinterestButton={showPinterestButton} />
      <EditorialContainer disableGutters>
        <div role="region" aria-label="About the Theme">
          <Typography
            variant="h5"
            sx={(theme) => ({
              margin: theme.spacing(3, 0, 1),
              textAlign: { xs: 'center', md: 'initial' },
            })}
          >
            About the Theme
          </Typography>
          <Box component="span" className="markdown">
            <Markdown
              components={{
                a: link,
                p: paragraph,
              }}
            >
              {theme.description}
            </Markdown>
          </Box>
          <SocialShareButtons
            title={`${theme.title} Party Theme | The Bash`}
            pinterestMedia={theme.image.url}
            url={`https://www.thebash.com/themes/${theme.slug}`}
          />
        </div>
      </EditorialContainer>
      {theme.video && (
        <Box
          sx={{
            position: 'relative',
            paddingTop: '56.25%',
            '& .player': { position: 'absolute', top: 0, left: 0 },
          }}
          role="region"
          aria-label="Theme Video"
        >
          <ReactPlayer
            className="player"
            controls
            width="100%"
            height="100%"
            src={theme.video}
            playsInline
            config={getReactPlayerConfig()}
            onError={handleVideoError}
          />
        </Box>
      )}
      <Box
        sx={{
          marginBottom: { xs: 2, md: 4 },
        }}
      >
        {theme.ingredients.map((ingredient, index) => (
          <ThemeIngredient
            themeName={theme.title}
            ingredient={ingredient}
            searchResult={searchResultCollections[ingredient.slug]}
            index={index}
            key={String(ingredient.id)}
            showPinterestButton={showPinterestButton}
          />
        ))}
      </Box>
      {theme.contentFooter && (
        <Box
          role="complementary"
          aria-label="Content Footer"
          sx={{
            marginBottom: { xs: 2, md: 4 },
          }}
        >
          <Markdown
            className="markdown"
            components={{
              a: link,
              p: paragraph,
            }}
          >
            {theme.contentFooter}
          </Markdown>
        </Box>
      )}
      <RelatedThemes
        analyticsProperties={analyticsProperties({
          carouselType: carouselTypes.relatedThemes,
          theme,
        })}
        relatedThemes={theme.relatedThemes}
        themeCount={themeCount}
      />
    </SectionContainer>
  );
};

ThemePage.propTypes = {
  theme: PropTypes.shape({
    title: PropTypes.string,
    ingredients: PropTypes.arrayOf(PropTypes.object),
    video: PropTypes.string,
  }),
  searchResultCollections: PropTypes.shape({}),
  themeCount: PropTypes.number,
};

ThemePage.defaultProps = {
  theme: {},
  searchResultCollections: {},
  themeCount: 0,
};

export default ThemePage;
